import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  Calendar as CalendarIcon,
  Clock,
  Plus,
  CreditCard,
  ChevronRight,
  Trash2,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
} from "lucide-react";
import {
  requestCancelBooking,
  updateSingleBookingStatus,
  updateBookingDate,
  updateBooking,
} from "../../services/bookings";
import { useAuth } from "../../auth/AuthContext";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from "../../components/ui/select";
import DatePopup from "../StartDateCalendar";
import { Customer } from "../../types/customer";
import GlobalLoader from "../ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import { cn } from "../../lib/utils";
import BackButton from "./BackButton";

interface Service {
  id: string;
  name: string;
  duration: string;
  price: string;
  time?: number;
  time_type?: string;
  staffIds?: number[];
  categoryId?: number;
  locationId?: number;
  description?: string;
  discount?: number;
  discount_type?: string;
  service_type?: string;
  tax_ids?: number[];
}

interface Props {
  selectedServices: Service[];
  selectedCustomer: Customer;
  bookingData?: any | null;
  setStep: (
    step:
      | "selection"
      | "customer"
      | "summary"
      | "payment"
      | "finalPayment"
      | "posPayment"
  ) => void;
  appointmentTime: string;
  tip: number;
  setTip: (tip: number) => void;
  onRebook: () => void;
  setIsAddingService: (value: boolean) => void;
  setSelectionMode?: (mode: "Services" | "Sunbeds") => void;
  selectionMode?: "Services" | "Sunbeds";
  stylist?: string;
  staffList?: { id: number; name: string; color: string }[];
  onSaveAsBooked?: () => Promise<void>;
  onRefresh?: () => void;
  setSelectedServices?: React.Dispatch<React.SetStateAction<Service[]>>;
  onClose?: () => void;
}

const SummaryStep: React.FC<Props> = ({
  selectedServices,
  selectedCustomer,
  bookingData,
  setStep,
  setIsAddingService,
  setSelectionMode,
  selectionMode,
  appointmentTime,
  stylist,
  staffList,
  onSaveAsBooked,
  onRefresh,
  setSelectedServices,
  onClose,
}) => {
  const { user } = useAuth();
  const currencySymbol =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "£";
  const toast = useToast();
  const [status, setStatus] = useState<string>(bookingData?.status || "booked");
  const [date, setDate] = useState<string>(
    bookingData?.date?.split(/[ T]/)[0] || ""
  );
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!bookingData) return;
    setLoading(true);
    try {
      const currentStylist = staffList?.find((s) => s.name === stylist);
      const employeeId = currentStylist?.id || staffList?.[0]?.id || 1;

      // Map selected services to the format backend expects for full update
      const item_ids = selectedServices.map((s) =>
        String(s.id).replace("service-", "").replace("sunbed-", "")
      );
      const types = selectedServices.map((s) =>
        String(s.id).includes("sunbed") ? "sunbed" : "service"
      );
      const cart_quantity = selectedServices.map(() => 1);
      const item_prices = selectedServices.map((s) => {
        const priceStr =
          typeof s.price === "string" ? s.price.replace(/[£$€]/g, "") : s.price;
        return Number(priceStr || 0);
      });

      const payload = {
        booking_date: date,
        hidden_booking_time: appointmentTime,
        status: status,
        employee_id: [employeeId],
        item_ids,
        types,
        cart_quantity,
        item_prices,
        paid_amount: totalPrice,
        payment_status: status === "completed" ? "completed" : "pending",
      };

      await updateBooking(bookingData.id, payload);

      toast.success("Booking updated successfully!");
      if (onRefresh) onRefresh();
      if (onClose) onClose();
    } catch (err: any) {
      console.error("Error updating booking full info:", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        "Failed to update booking services.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const totalPrice = selectedServices.reduce((sum, s) => {
    if (!s || !s.price) return sum;
    const priceStr =
      typeof s.price === "string" ? s.price.replace(/[£$€]/g, "") : s.price;
    return sum + Number(priceStr || 0);
  }, 0);

  const getInitials = (name: string) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="absolute inset-0 flex flex-col bg-white overflow-hidden">
      <div className="flex-1 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden custom-scrollbar">
        {/* LEFT PANEL - Customer Profile & Actions */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full lg:w-[35%] bg-white lg:border-r border-gray-100 flex flex-col flex-shrink-0"
        >
          <div className="lg:flex-1 lg:overflow-y-auto p-6 sm:p-8 custom-scrollbar">
            <div className="flex flex-col items-center text-center space-y-4 mb-10">
              <div className="w-24 h-24 rounded-full bg-[#885ABB]/10 flex items-center justify-center text-[#885ABB] text-3xl font-bold shadow-sm border-4 border-white">
                {getInitials(selectedCustomer.name)}
              </div>
              <div>
                <h3 className="text-xl font-[600] text-[#402B69] mb-1">
                  {selectedCustomer.name}
                </h3>
                <p className="text-sm text-gray-400 font-medium">
                  {selectedCustomer.email || "No email provided"}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">
                  Contact Info
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 text-sm text-gray-500 font-medium">
                    <Phone size={14} />{" "}
                    <span>{selectedCustomer.phone || "Not provided"}</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 text-sm text-gray-500 font-medium">
                    <Mail size={14} />{" "}
                    <span>{selectedCustomer.email || "Not provided"}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">
                  Appointment Details
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-[#885ABB]/5 text-sm text-[#885ABB] font-bold">
                    <div className="flex items-center gap-3">
                      <Clock size={14} /> <span>{appointmentTime}</span>
                    </div>
                    <span className="text-[10px] uppercase">Slot</span>
                  </div>
                </div>
              </div>

              {bookingData && (
                <div className="pt-4 space-y-3">
                  <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">
                    Manage Status
                  </h4>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="h-11 border border-gray-100 rounded-xl text-sm font-semibold text-[#402B69] bg-gray-50">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-100 rounded-xl">
                      <SelectItem value="booked">Booked</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {!bookingData && (
            <div className="px-6 sm:px-8 pb-8 sm:pb-6 pt-2 sticky bottom-0 bg-white lg:static border-t lg:border-t-0 border-gray-50 z-20">
              <button
                onClick={() => setStep("customer")}
                className="w-full h-12 bg-white border border-gray-200 text-[#402B69] rounded-xl font-bold hover:bg-gray-50 transition-all text-sm flex items-center justify-center gap-2 shadow-sm"
              >
                <User size={16} />
                Change Customer
              </button>
            </div>
          )}
        </motion.div>

        {/* RIGHT PANEL - Services & Summary */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex-1 bg-[#F9F9F9] flex flex-col relative lg:overflow-hidden"
        >
          {/* Internal Header for Right Panel */}
          <div className="px-4 lg:px-6 pt-4 lg:pt-8 pb-3 lg:pb-5 bg-white border-b border-gray-100 shadow-sm relative z-20">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <div>
                <h2 className="text-lg lg:text-[22px] font-bold text-[#402B69] leading-tight">
                  {selectionMode === "Sunbeds"
                    ? "Add Sunbed Session"
                    : "Add Appointment"}
                </h2>
                <div className="flex items-center gap-2 mt-0.5 lg:mt-1">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    Step 3 of 4: Summary
                  </span>
                </div>
              </div>
              <BackButton onClick={() => setStep("customer")} />
            </div>

            <div className="flex items-center gap-1">
              {[0, 1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={cn(
                    "h-1 lg:h-1.5 flex-1 rounded-full transition-all duration-500",
                    s <= 2 ? "bg-[#885ABB]" : "bg-gray-100"
                  )}
                />
              ))}
            </div>
          </div>

          <div className="flex-1 lg:overflow-y-auto custom-scrollbar">
            <div className="space-y-6 p-4 sm:p-6 pb-2">
              <section className="space-y-4">
                <div className="flex justify-between items-center px-1">
                  <h4 className="text-[11px] font-[600] text-[#402B69] uppercase tracking-wider opacity-60">
                    Services
                  </h4>
                  <span className="text-[10px] font-bold text-[#885ABB] bg-white border border-[#885ABB]/20 px-2.5 py-1 rounded-full">
                    {selectedServices.length} Selected
                  </span>
                </div>

                <div className="space-y-2">
                  {selectedServices.filter(Boolean).map((service, idx) => (
                    <div
                      key={service.id + idx}
                      className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-3 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-1.5 h-8 bg-[#885ABB] rounded-full" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-[600] text-[#402B69] truncate uppercase tracking-tight">
                              {service.name}
                            </p>
                            <div className="flex items-center gap-2 text-[10px] text-gray-400 font-medium mt-0.5">
                              <Clock size={10} /> <span>{service.duration}</span>
                              <span className="w-1 h-1 rounded-full bg-gray-300 mx-0.5" />
                              <User size={10} />{" "}
                              <span className="truncate">
                                {stylist || "Not assigned"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-base font-bold text-[#885ABB] whitespace-nowrap">
                            {currencySymbol}
                            {Number(
                              typeof service.price === "string"
                                ? service.price.replace(/[£$€]/g, "")
                                : service.price || 0
                            ).toFixed(2)}
                          </span>
                          {setSelectedServices && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedServices((prev) =>
                                  prev.filter((_, i) => i !== idx)
                                );
                              }}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove Service"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {(!bookingData || status === 'booked') && (
                  <button
                    onClick={() => {
                      setIsAddingService(true);
                      if (setSelectionMode && selectionMode !== "Sunbeds")
                        setSelectionMode("Services");
                      setStep("selection");
                    }}
                    className="w-full py-4 border-2 border-dashed border-gray-100 rounded-xl text-gray-400 font-[600] text-xs hover:border-[#885ABB]/50 hover:bg-white transition-all flex items-center justify-center gap-2 bg-transparent"
                  >
                    <Plus size={14} />
                    {selectionMode === "Sunbeds"
                      ? "Add another sunbed"
                      : "Add another service"}
                  </button>
                )}
              </section>
            </div>
          </div>

          {/* FOOTER - NOW INSIDE RIGHT PANEL */}
          <div className="bg-white shadow-[0_-10px_30px_rgba(0,0,0,0.03)] border-t border-gray-100 flex flex-col w-full z-10 sticky bottom-0 lg:static">
            {/* Order Summary Section */}
            <div className="px-4 sm:px-6 pt-4 pb-2">
              <section className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm space-y-3">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-gray-500 font-medium">
                    <span>Total Items ({selectedServices.length})</span>
                    <span>
                      {currencySymbol}
                      {totalPrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Taxes & Fees</span>
                    <span>{currencySymbol}0.00</span>
                  </div>
                  <div className="pt-3 border-t border-gray-50 flex justify-between items-center">
                    <span className="text-sm sm:text-base font-[600] text-[#402B69]">
                      Total Due
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-[#885ABB]">
                      {currencySymbol}
                      {totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </section>
            </div>

            {/* Action Buttons */}
            <div className="p-4 sm:p-6 pt-2 pb-8 sm:pb-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
              {bookingData ? (
                <button
                  onClick={handleSave}
                  disabled={loading || selectedServices.length === 0}
                  className="w-full sm:flex-1 h-12 bg-white border border-gray-100 text-[#402B69] rounded-xl font-bold hover:bg-gray-50 transition-all text-sm shadow-sm"
                >
                  Update
                </button>
              ) : (
                <button
                  onClick={async () => {
                    if (onSaveAsBooked) {
                      setLoading(true);
                      try {
                        await onSaveAsBooked();
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  disabled={loading || selectedServices.length === 0}
                  className="sm:flex-1 h-12 bg-white border border-[#885ABB] text-[#885ABB] rounded-xl font-bold hover:bg-[#885ABB]/5 transition-all text-[11px] sm:text-sm"
                >
                  Save
                </button>
              )}
              <button
                onClick={() => setStep("payment")}
                disabled={loading || selectedServices.length === 0}
                className="w-full sm:flex-[1.5] h-12 bg-[#885ABB] text-white rounded-xl font-bold hover:bg-[#7748A4] shadow-lg shadow-[#885ABB]/10 transition-all flex items-center justify-center gap-2 group text-sm"
              >
                <CreditCard size={16} />
                <span>Proceed to Checkout</span>
                <ChevronRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {loading && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-white/40 backdrop-blur-[2px]">
          <GlobalLoader
            initialPageLoad={false}
            description="Processing..."
            compensateSidebar={false}
          />
        </div>
      )}
    </div>
  );
};

export default SummaryStep;
